// src/features/learner/components/ContentRenderers/InteractiveImageRenderer.js
import React, { useRef } from 'react';
import { AudioPlayer } from '../../../../features/MediaLibrary';
import SpeechBubble from '../../../units/SpeechBubble';

export const InteractiveImageRenderer = ({
    unit,
    selectedHotspot,
    visitedHotspots,
    onHotspotClick,
    onSetSelectedHotspot
}) => {
    const imageContainerRef = useRef(null);

    // Parse content
    let content;
    try {
        content = typeof unit.content === 'string' ? JSON.parse(unit.content) : unit.content;
    } catch (error) {
        console.error('Error parsing interactive image content:', error);
        content = {};
    }

    const { mainText = '', mainAudio = '', imageUrl = '', hotspots = [] } = content;

    const handleHotspotClick = (hotspot) => {
        // Strip [AUDIO:url] tokens from display text before passing up
        const cleanedHotspot = {
            ...hotspot,
            text: hotspot.text ? hotspot.text.replace(/\[AUDIO:[^\]]+\]/g, '').trim() : hotspot.text
        };
        if (onHotspotClick) onHotspotClick(cleanedHotspot);
    };

    return (
        <div className="interactive-image-content">
            
            {/* Main text and audio */}
            {mainText && (
                <div className="main-text-display">
                    <div className="main-text">{mainText}</div>
                    {/* Shared AudioPlayer — coordinates with GlobalAudioManager */}
                    {mainAudio && (
                        <AudioPlayer audioUrl={mainAudio} />
                    )}
                </div>
            )}

            {/* Interactive image */}
            {imageUrl && (
                <div className="image-container">
                    <div
                        className="interactive-image-wrapper"
                        ref={imageContainerRef}
                        style={{ position: 'relative' }}
                    >
                        <img
                            src={imageUrl}
                            alt="Interactive content"
                            className="interactive-image"
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: 'auto',
                                maxWidth: '600px',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                display: 'block'
                            }}
                        />

                        {/* Hotspot markers */}
                        {hotspots.map(hotspot => {
                            const isVisited = visitedHotspots.has(hotspot.id);
                            const isSelected = selectedHotspot === hotspot.id;

                            return (
                                <div
                                    key={hotspot.id}
                                    onClick={(e) => { e.stopPropagation(); handleHotspotClick(hotspot); }}
                                    className={`hotspot ${isVisited ? 'visited' : ''} ${isSelected ? 'selected' : ''}`}
                                    style={{
                                        position: 'absolute',
                                        left: `${hotspot.x}%`,
                                        top: `${hotspot.y}%`,
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 15,
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '50%',
                                        background: isVisited
                                            ? 'linear-gradient(135deg, #28a745, #20c997)'
                                            : 'linear-gradient(135deg, #4facfe, #00f2fe)',
                                        border: '3px solid white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                        transition: 'all 0.3s ease'
                                    }}
                                />
                            );
                        })}

                        {/* Speech bubble — audio inside hotspot played via AudioPlayer
                            embedded in the bubble's text if needed in future;
                            for now SpeechBubble shows text only (as designed) */}
                        {selectedHotspot && (() => {
                            const raw = hotspots.find(h => h.id === selectedHotspot);
                            if (!raw) return null;

                            // Separate display text from any embedded audio URL
                            const audioUrl = raw.audioUrl ||
                                (raw.text?.match(/\[AUDIO:([^\]]+)\]/)?.[1]?.trim()) ||
                                null;

                            const cleanedHotspot = {
                                ...raw,
                                text: raw.text
                                    ? raw.text.replace(/\[AUDIO:[^\]]+\]/g, '').trim()
                                    : raw.text
                            };

                            return (
                                <>
                                    <SpeechBubble
                                        hotspot={cleanedHotspot}
                                        onClose={() => onSetSelectedHotspot && onSetSelectedHotspot(null)}
                                        containerRef={imageContainerRef}
                                    />
                                    {/* Hotspot audio player — rendered outside the bubble
                                        so it doesn't interfere with bubble positioning */}
                                    {audioUrl && (
                                        <div style={{
                                            position: 'absolute',
                                            left: `${raw.x}%`,
                                            top: `calc(${raw.y}% + 20px)`,
                                            transform: 'translateX(-50%)',
                                            zIndex: 20
                                        }}>
                                            <AudioPlayer audioUrl={audioUrl} />
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Progress indicator */}
            <div className="interactive-progress">
                <div className="progress-stats">
                    <p>{visitedHotspots.size} of {hotspots.length} responses explored</p>
                </div>
                <div className="progress-bar">
                    <div
                        className="progress-fill"
                        style={{
                            width: hotspots.length > 0
                                ? `${(visitedHotspots.size / hotspots.length) * 100}%`
                                : '0%'
                        }}
                    />
                </div>
                {visitedHotspots.size === hotspots.length && hotspots.length > 0 && (
                    <div className="completion-message">🎉 All responses explored!</div>
                )}
            </div>
        </div>
    );
};